import React from "react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@capacit-com/lara-law-shared-ui";
import {
  SearchResult,
  LegislationSearchResult,
} from "@/models/search/SearchResults";
import { getResultData } from "../../utils/resultDataExtractors";
import {
  getEntitySpecificInfo,
  formatDate,
} from "../../utils/entityInfoHelpers";
import { getArvidSummary } from "../../utils/metadataHelpers";
import { useSearchResultActions } from "../../hooks/useSearchResultActions";
import { NEW_TAB_MODIFIER_TOOLTIP } from "@/lib/navigation/appNavigation";
import useConsolidationRenderingEnabled from "@/hooks/useConsolidationRenderingEnabled";
import { getSearchResultConsolidationDisplay } from "@/lib/consolidationLegislationUtils";
import ConsolidationProgressBadge from "@/pages/legislation/components/ConsolidationProgressBadge";
import { IndexingStatusIcon } from "@/components/indexing-status/IndexingStatusIcon";
import { useIndexingOperationsStore } from "@/stores/indexing-operations.store";

interface SearchResultItemProps {
  result: SearchResult;
  isLast?: boolean;
  lastElementRef?: (node: HTMLDivElement | null) => void;
  onResultClick?: () => void;
}

const getValidityIndicator = (resultData: LegislationSearchResult | null) => {
  if (resultData?.legislationValidityScheme === undefined) return;

  const validity = resultData?.legislationValidityScheme?.isHistorical
    ? "Historisk"
    : resultData?.legislationValidityScheme?.isNullified
      ? "Ophævet"
      : resultData?.legislationValidityScheme?.isRevoked
        ? "Ugyldig"
        : "";

  if (validity !== "") {
    return (
      <div className="text-xs rounded-md border-2 border-foreground p-1 bg-foreground text-background">
        {validity}
      </div>
    );
  }
};

const SearchResultItem = ({
  result,
  isLast = false,
  lastElementRef,
  onResultClick,
}: SearchResultItemProps) => {
  const { handleResultClick } = useSearchResultActions();
  const { enabled: consolidationFeatureEnabled } =
    useConsolidationRenderingEnabled();

  const resultData = getResultData(result);
  const entityInfo = getEntitySpecificInfo(result);
  const entityParts = entityInfo.parts ?? [];

  const arvidSummary = getArvidSummary(result);

  const consolidationDisplay =
    consolidationFeatureEnabled && result.legislation
      ? getSearchResultConsolidationDisplay(result.legislation)
      : null;

  // The same amber/red index marker as the files tab and the Workspaces page search
  // (LAR-235); a healthy document shows nothing. A run started this session spins.
  const startedOperations = useIndexingOperationsStore(
    (state) => state.operations
  );
  const workspaceDocument = result.workspaceDocument;
  const indexStatus = workspaceDocument ? (
    <IndexingStatusIcon
      hideHealthy
      isIndexed={workspaceDocument.isIndexed ?? true}
      searchIndexStale={workspaceDocument.searchIndexStale ?? false}
      documentType={workspaceDocument.documentType}
      isIndexing={workspaceDocument.id in startedOperations}
    />
  ) : null;

  const handleClick = (event: React.MouseEvent) => {
    // Mark that a result was clicked (to prevent duplicate save on modal close)
    onResultClick?.();
    // Handle the actual result click (navigation + save)
    handleResultClick(result, event);
  };

  const resultItemContent = (
    <div
      className="group/result hover:bg-accent hover:text-accent-foreground py-2 px-3 mb-1 rounded-sm text-sm cursor-pointer min-w-0"
      onClick={handleClick}
      title={NEW_TAB_MODIFIER_TOOLTIP}
      ref={isLast && lastElementRef ? lastElementRef : null}
    >
      {/* Title row with truncation */}
      {resultData.title === "" ? (
        <div className="mb-1 flex gap-2 items-center min-w-0 overflow-hidden">
          <span
            className="font-medium truncate italic min-w-0 flex-1"
            title="Ingen titel"
          >
            Ingen titel
          </span>
          <div className="shrink-0 flex items-center gap-2">
            {indexStatus}
            {consolidationDisplay && (
              <ConsolidationProgressBadge
                display={consolidationDisplay}
                size="xs"
                showLastVerified={false}
              />
            )}
            {getValidityIndicator(resultData.data as LegislationSearchResult)}
          </div>
        </div>
      ) : (
        <div className="mb-1 flex gap-2 items-center min-w-0 overflow-hidden">
          <span
            className="font-medium truncate min-w-0 flex-1"
            title={resultData.title}
          >
            {resultData.title}
          </span>
          <div className="shrink-0 flex items-center gap-2">
            {indexStatus}
            {consolidationDisplay && (
              <ConsolidationProgressBadge
                display={consolidationDisplay}
                size="xs"
                showLastVerified={false}
              />
            )}
            {getValidityIndicator(resultData.data as LegislationSearchResult)}
          </div>
        </div>
      )}

      {/* Bottom row with dates and entity-specific info */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground group-hover/result:text-inherit min-w-0 overflow-hidden">
        {/* Dates - fixed */}
        {resultData.createdAt && (
          <span className="shrink-0 whitespace-nowrap">
            Oprettet: {formatDate(resultData.createdAt)}
          </span>
        )}

        {resultData.createdAt && resultData.updatedAt && (
          <span className="shrink-0">•</span>
        )}

        {resultData.updatedAt && (
          <span className="shrink-0 whitespace-nowrap">
            Opdateret: {formatDate(resultData.updatedAt)}
          </span>
        )}

        {(resultData.createdAt || resultData.updatedAt) &&
          (entityInfo.source || entityParts.length > 0) && (
            <span className="shrink-0">•</span>
          )}

        {/* Source - truncates FIRST */}
        {entityInfo.source && (
          <span
            className="min-w-0 max-w-fit flex-1 truncate"
            title={entityInfo.source}
          >
            {"Kilde: " + entityInfo.source}
          </span>
        )}

        {/* Bullet after source if there are parts */}
        {entityInfo.source && entityParts.length > 0 && (
          <span className="shrink-0">•</span>
        )}

        {/* Other entity parts - fixed (do not shrink) */}
        {entityParts.map((part, idx) => (
          <React.Fragment key={idx}>
            <span className="shrink-0 whitespace-nowrap">{part}</span>
            {idx < entityParts.length - 1 && (
              <span className="shrink-0">•</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // Wrap with Tooltip if Arvid summary exists
  if (arvidSummary) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{resultItemContent}</TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-md">
            <div
              className="text-sm"
              dangerouslySetInnerHTML={{ __html: arvidSummary }}
            />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return resultItemContent;
};

export default SearchResultItem;
